import React, { useState } from "react";
import {
  ReportDetail,
  ReportScheduleType,
  WindowReport,
} from "../types/batchSession.types";

const SCHEDULE_TYPES: ReportScheduleType[] = [
  "All Days",
  "Weekdays",
  "Weekends",
  "Flexible",
];

const fmtTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZone: "Asia/Kolkata",
      })
    : "—";

function Yes({ on, label }: { on: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
        on
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-50 text-slate-400 border-slate-200"
      }`}
    >
      {label}: {on ? "Yes" : "No"}
    </span>
  );
}

/* Trainer in the meeting AND recording on = automation is working, whatever
 * the server-side status still says (it stays "pending" until the session ends). */
function StatusBadge({ d }: { d: ReportDetail }) {
  const working = d.presence.trainerPresent && d.presence.recordingOn;
  const label = working ? "working" : d.automationStatus;
  const map: Record<string, string> = {
    working: "bg-emerald-50 text-emerald-700 border-emerald-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-amber-50 text-amber-700 border-amber-200",
    failed: "bg-red-50 text-red-700 border-red-200",
    pending: "bg-slate-50 text-slate-500 border-slate-200",
    not_applicable: "bg-slate-50 text-slate-500 border-slate-200",
  };
  return (
    <span
      title={working ? "Trainer joined and recording is on" : d.failureReason}
      className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${map[label]}`}
    >
      {label}
    </span>
  );
}

interface Props {
  report: WindowReport | null;
  error: boolean;
}

export default function AutomationReport({ report, error }: Props) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"window" | "live">("window");

  const a = report?.automation;
  const rows = tab === "live" ? report?.live : report?.details;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-5">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <div>
          <p className="text-sm font-bold text-slate-800">
            Automation Report · last {report?.windowHours ?? 12}h
          </p>
          <p className="text-xs text-slate-500">
            {error
              ? "Could not load report"
              : !report
                ? "Loading…"
                : `${a!.automated} of ${a!.expected - a!.pending} due sessions picked up by automation (${a!.automationRate}%) · ${a!.manual} manual · ${a!.missed} missed · updated ${fmtTime(report.generatedAt)}`}
          </p>
        </div>
        <span className="text-slate-400 text-sm">{open ? "▲" : "▼"}</span>
      </button>

      {open && report && (
        <div className="px-5 pb-5 border-t border-slate-100">
          <div className="overflow-x-auto mt-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase text-slate-500 text-left">
                  <th className="py-2 pr-4">Schedule</th>
                  <th className="pr-4">Expected</th>
                  <th className="pr-4">Automated</th>
                  <th className="pr-4">Manual join</th>
                  <th className="pr-4">Missed</th>
                  <th className="pr-4">Pending</th>
                  <th className="pr-4">Success</th>
                  <th className="pr-4">Partial</th>
                  <th className="pr-4">Failed</th>
                  <th>Automation %</th>
                </tr>
              </thead>
              <tbody>
                {SCHEDULE_TYPES.map((t) => {
                  const e = report.byScheduleType[t];
                  return (
                    <tr key={t} className="border-t border-slate-100">
                      <td className="py-2 pr-4 font-semibold">{t}</td>
                      <td className="pr-4">{e.expected}</td>
                      <td className="pr-4">{e.automated}</td>
                      <td className="pr-4">{e.manual}</td>
                      <td className="pr-4 text-red-600">{e.missed}</td>
                      <td className="pr-4">{e.pending}</td>
                      <td className="pr-4 text-emerald-700">{e.success}</td>
                      <td className="pr-4 text-amber-700">{e.partial}</td>
                      <td className="pr-4 text-red-600">{e.failed}</td>
                      <td className="font-semibold">{e.automationRate}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 mt-5 mb-3">
            {(["window", "live"] as const).map((k) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold border ${
                  tab === k
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-200"
                }`}
              >
                {k === "window"
                  ? `Due in window (${report.details.length})`
                  : `In meeting now (${report.live.length})`}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto max-h-80 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="text-xs uppercase text-slate-500 text-left">
                  <th className="py-2 pr-4">Batch</th>
                  <th className="pr-4">Schedule</th>
                  <th className="pr-4">Due</th>
                  <th className="pr-4">Created by</th>
                  <th className="pr-4">Presence</th>
                  <th>Automation</th>
                </tr>
              </thead>
              <tbody>
                {(rows || []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-slate-400">
                      Nothing to show
                    </td>
                  </tr>
                )}
                {(rows || []).map((d) => (
                  <tr key={`${d.batchNo}-${d.expectedStartAt}-${d.sessionId}`} className="border-t border-slate-100">
                    <td className="py-2 pr-4 font-semibold">
                      {d.batchNo}
                      <span className="block text-xs font-normal text-slate-500">{d.courseName}</span>
                    </td>
                    <td className="pr-4">{d.scheduleType}</td>
                    <td className="pr-4">{fmtTime(d.expectedStartAt)}</td>
                    <td className="pr-4">
                      {!d.sessionId ? "—" : d.isAutomated ? "Automation" : "Manual join"}
                    </td>
                    <td className="pr-4">
                      <div className="flex flex-wrap gap-1">
                        <Yes on={d.presence.trainerPresent} label="Trainer" />
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200">
                          Students: {d.presence.studentsPresent}/{d.students.expected}
                        </span>
                        <Yes on={d.presence.recordingOn} label="Recording" />
                      </div>
                    </td>
                    <td><StatusBadge d={d} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
